/**
 * Processamento de REPORTS do OrdersRouter (bloco order-domain 🔴) — extraído
 * de ordersRouter.jsx na modularização dos god-objects: classificadores de
 * OrdStatus FIX (0/1/5 aberto, 2 executado, 4 cancelado) e receiveReport — o
 * pipeline completo do push de execução: gate offline/manualLogout,
 * enriquecimento de replace via recentModifications, logs/toasts/sons por
 * estado, tradução de rejeição do broker e fan-out para os receivers
 * (orderStore etc.).
 *
 * Corpos preservados byte-a-byte, `this` → `rt`; estado permanece NA
 * INSTÂNCIA Router; chamadas internas via `rt.<método>` (delegates).
 * Contratos REAIS que pinam este seam (verdes antes E depois do corte):
 * orderReportSeam.integration.test.js (router.receiveReport(STRING) →
 * orderStore real), orderStoreReceiver.test.js, orderStore.test.js,
 * pendingOtoSymbolCollision.proof.test.js.
 */
import performanceMonitor from "../../utils/performanceMonitor.js";
import {
  extractOrderSide,
  extractQuantity,
  extractAvgPrice,
} from "./routerReportFields.js";
import logger from "../../utils/logger.js";
import { playSound } from "../../utils/soundUtils.js";
import { globalToast } from "../../components/ToastProvider.jsx";
import {
  getOrderSideText,
  translateBrokerMessage,
  translateRejectionReason,
} from "../../utils/orderStyles.js";

export const isOpenOrder = (rt, report) => {
  return (
    report.OrdStatus == "0" ||
    report.OrdStatus == "1" ||
    report.OrdStatus == "5"
  );
};

export const isFilledOrder = (rt, report) => {
  return report.OrdStatus == "2";
};

export const isCancelledOrder = (rt, report) => {
  return report.OrdStatus == "4";
};

export const isFinalStatusOrder = (rt, report) => {
  return rt.isFilledOrder(report) || rt.isCancelledOrder(report);
};

export const receiveReport = (rt, dataString) => {
  // Ignore reports if router is offline or manually logged out
  if (rt.state === "offline" || rt.manualLogout) {
    if (import.meta.env.DEV) {
      console.log(
        `[${new Date().toISOString()}] 🚫 Ignoring order report - router offline or manually logged out`,
      );
    }
    return;
  }

  var report = JSON.parse(dataString);

  // Track report event for performance monitoring
  performanceMonitor.trackReport();

  // Debug logging - only in DEV with VITE_VERBOSE_LOGS to avoid console floods
  if (import.meta.env.DEV && import.meta.env.VITE_VERBOSE_LOGS) {
    console.log("📩 [receiveReport] Raw JSON:", dataString);
    console.log("📩 [receiveReport] Parsed object:", report);
    if (
      report &&
      (report.state === "replaced" || report.execType === "REPLACED")
    ) {
      const summary = {
        id: report.id,
        symbol: report.symbol,
        price: report.price || report.limit || report.stop,
        limit: report.limit,
        stop: report.stop,
        quantity: report.quantity || report.qty,
        state: report.state,
        tif: report.tif,
        remark: report.remark,
      };
      console.log("🔄 [receiveReport] Replace summary:", summary);
    }
  }

  // Check for successful order modifications - unified handling
  if (report && report.state === "replaced") {
    const orderSide = extractOrderSide(report);
    let currentPrice = report.price || report.limit || report.stop;
    const quantity = extractQuantity(report);

    // Try to enrich from recent modifications if report is missing data
    let symbol = report.symbol;
    let otoLogText = "";
    let otoLogData = {};
    const recentMod = rt.recentModifications.find((mod) => {
      const sideMatch =
        (mod.side?.toLowerCase() === "buy" && orderSide === "BUY") ||
        (mod.side?.toLowerCase() === "sell" && orderSide === "SELL");
      const quantityMatch = mod.quantity === quantity;
      const recentEnough = Date.now() - mod.timestamp <= 15000;
      return sideMatch && quantityMatch && recentEnough;
    });
    if (recentMod) {
      if (!symbol) symbol = recentMod.symbol;
      if (!currentPrice) currentPrice = recentMod.price;
      // Extract OTO gain/loss from cached modification
      if (recentMod.gainPrice) otoLogData.gainPrice = recentMod.gainPrice;
      if (recentMod.lossPrice) otoLogData.lossPrice = recentMod.lossPrice;
      const parts = [];
      if (recentMod.gainPrice) parts.push(`Gain: ${recentMod.gainPrice}`);
      if (recentMod.lossPrice) parts.push(`Loss: ${recentMod.lossPrice}`);
      if (parts.length > 0) otoLogText = ` | OTO [${parts.join(", ")}]`;
      // Remove used modification
      const index = rt.recentModifications.indexOf(recentMod);
      rt.recentModifications.splice(index, 1);
    }

    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Ordem modificada com sucesso: ${orderSideTranslated} ${symbol || "N/A"} - ${quantity} @ ${currentPrice || "N/A"}${otoLogText}`;

    logger.orders.success(logMessage, {
      symbol: symbol || "N/A",
      price: currentPrice,
      quantity: quantity,
      orderType: orderSide,
      orderState: report.state || "N/A",
      orderId: report.id || "N/A",
      broker: rt.broker,
      message: report.message || "N/A",
      timestamp: new Date().toISOString(),
      ...otoLogData,
    });
    playSound("ping", 0.3);
    // Compact one-line toast — keeps multiple simultaneous order
    // modifications from taking over the top-right of the chart.
    const toastMessage = `Ordem modificada · ${orderSideTranslated} ${symbol || "N/A"} ${quantity} @ ${currentPrice || "N/A"}`;
    // Successful modification — match the success classification used in the
    // log above (green), not the generic info-blue default (#343).
    globalToast.showCompact(toastMessage, "success");
  }

  // Log filled orders with detailed information
  if (report && report.state && report.state == "filled") {
    const orderSide = extractOrderSide(report);
    const quantity = extractQuantity(report);
    const avgPrice = extractAvgPrice(report);
    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Ordem executada: ${orderSideTranslated} ${report.symbol} - ${quantity} @ ${avgPrice}`;

    logger.orders.success(logMessage, {
      symbol: report.symbol || "N/A",
      price: avgPrice,
      avgPrice: avgPrice,
      quantity: quantity,
      orderType: orderSide,
      orderState: report.state || "N/A",
      orderId: report.id || "N/A",
      broker: rt.broker,
      timestamp: new Date().toISOString(),
    });
    playSound("coin", 0.3);
    globalToast.showSuccess(logMessage, "Ordem Executada");
  }
  // Log cancelled orders
  else if (
    report &&
    report.state &&
    (report.state == "cancelled" || report.state == "canceled")
  ) {
    const orderSide = extractOrderSide(report);
    const quantity = extractQuantity(report);
    // Format price properly: check limit, stop, or market order
    const orderPrice = report.limit || report.stop || report.price || "MERCADO";
    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Ordem cancelada: ${orderSideTranslated} ${report.symbol} - ${quantity} @ ${orderPrice}`;

    logger.orders.success(logMessage, {
      symbol: report.symbol || "N/A",
      price: orderPrice,
      quantity: quantity,
      orderType: orderSide,
      orderState: report.state || "N/A",
      orderId: report.id || "N/A",
      broker: rt.broker,
      timestamp: new Date().toISOString(),
    });
    globalToast.showSuccess(logMessage, "Ordem Cancelada");
  }
  // Log rejected orders
  else if (
    report &&
    report.state &&
    (report.state == "rejected" || report.state == "reject")
  ) {
    const orderSide = extractOrderSide(report);
    const quantity = extractQuantity(report);

    // Try to get symbol and price from multiple sources
    let symbol = report.symbol;
    let price = report.price || report.limit || report.stop;

    // Try recent modifications first
    if (!symbol) {
      const recentMod = rt.recentModifications.find((mod) => {
        const sideMatch =
          (mod.side?.toLowerCase() === "buy" && orderSide === "BUY") ||
          (mod.side?.toLowerCase() === "sell" && orderSide === "SELL");
        const quantityMatch = mod.quantity === quantity;
        const recentEnough = Date.now() - mod.timestamp <= 15000;
        return sideMatch && quantityMatch && recentEnough;
      });

      if (recentMod) {
        symbol = recentMod.symbol;
        if (!price) price = recentMod.price;
        // Remove used modification
        const index = rt.recentModifications.indexOf(recentMod);
        rt.recentModifications.splice(index, 1);
      }
    }

    // Fallback: search in current account data
    if (!symbol && rt.accountData && rt.accountData.openOrders) {
      for (const [orderSymbol, orders] of Object.entries(
        rt.accountData.openOrders,
      )) {
        const matchingOrder = orders.find((order) => {
          const orderAction = (order.action || order.side || "").toUpperCase();
          const sideMatch =
            (orderAction.includes("BUY") && orderSide === "BUY") ||
            (orderAction.includes("SELL") && orderSide === "SELL");
          const qtyMatch = order.quantity === quantity;
          return sideMatch && qtyMatch;
        });

        if (matchingOrder) {
          symbol = orderSymbol;
          if (!price) {
            price =
              matchingOrder.limit || matchingOrder.stop || matchingOrder.price;
          }
          break;
        }
      }
    }

    // Final fallbacks
    symbol = symbol || "SÍMBOLO_DESCONHECIDO";
    price = price || "MERCADO";

    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Ordem rejeitada: ${orderSideTranslated} ${symbol} - ${quantity} @ ${price}`;

    // #366: surface the broker's reason to the user (was log-only). Photon
    // jargon is translated to a plain explanation; other reasons pass through.
    const rawReason = report.reason || report.message;
    const friendlyReason = translateRejectionReason(rawReason);

    logger.orders.error(logMessage, {
      symbol: symbol,
      price: price,
      quantity: quantity,
      orderType: orderSide,
      orderState: report.state || "N/A",
      orderId: report.id || "N/A",
      broker: rt.broker,
      reason: rawReason || "Motivo não especificado", // cru p/ telemetria/debug
      friendlyReason: friendlyReason, // traduzido p/ exibição no painel de log
      timestamp: new Date().toISOString(),
    });
    playSound("bump", 0.3);
    globalToast.showError(
      friendlyReason ? `${logMessage}\n${friendlyReason}` : logMessage,
      "Ordem Rejeitada",
    );
  }
  // Log partially filled orders. Server sends OrdStatus 1 as " partialFilled"
  // (leading space, camelCase) — normalize trim+case so it matches (not "partial").
  else if (
    report &&
    report.state &&
    ["partial", "partialfilled"].includes(report.state.trim().toLowerCase())
  ) {
    const orderSide = extractOrderSide(report);
    const quantity = extractQuantity(report);
    const filledQty = report.filledQuantity || report.filled || report.qtt || 0;
    const avgPrice = extractAvgPrice(report);
    // Translate order side to Portuguese
    const orderSideTranslated = getOrderSideText(orderSide.toLowerCase());
    const logMessage = `Ordem parcialmente executada: ${orderSideTranslated} ${report.symbol} - ${filledQty}/${quantity} @ ${avgPrice}`;

    logger.orders.warning(logMessage, {
      symbol: report.symbol || "N/A",
      price: report.price || "N/A",
      avgPrice: avgPrice,
      quantity: quantity,
      filledQuantity: filledQty,
      remainingQuantity:
        report.remainingQuantity || quantity - filledQty || "N/A",
      orderType: orderSide,
      orderState: report.state || "N/A",
      orderId: report.id || "N/A",
      broker: rt.broker,
      timestamp: new Date().toISOString(),
    });
    globalToast.showWarning(logMessage, "Execução Parcial");
  }
  // Log any remaining intermediate lifecycle state so the order log reflects
  // every broker step (#364): "open"/"new"/"pending"/"active" and stop-trigger
  // acks like "Stop limit order was triggered.". These arrive as separate rpt
  // events (server confirms both gates pass) but fell through the whitelist
  // above and were silently forwarded. Info level → no sound/toast, panel-only.
  // "replaced" is excluded because the independent `if` above already logs it.
  else if (report && report.state && report.state !== "replaced") {
    const orderSide = (
      report.side ||
      report.orderType ||
      report.type ||
      report.action ||
      ""
    ).toUpperCase();
    const orderSideTranslated = orderSide
      ? getOrderSideText(orderSide.toLowerCase())
      : "";
    // report.message carries the broker's FIX tag 58 text (e.g. "was
    // triggered.") except when the server overrides it with a generic PT
    // string; fall back to the raw state so the entry is never empty.
    const brokerText =
      report.message && report.message !== "N/A"
        ? translateBrokerMessage(report.message)
        : `Ordem atualizada (${report.state})`;
    const context = [orderSideTranslated, report.symbol]
      .filter(Boolean)
      .join(" ");
    const logMessage = context ? `${brokerText} — ${context}` : brokerText;

    logger.orders.info(logMessage, {
      symbol: report.symbol || "N/A",
      orderState: report.state || "N/A",
      orderId: report.id || "N/A",
      message: report.message || "N/A",
      broker: rt.broker,
      timestamp: new Date().toISOString(),
    });
  }
  // Forward report to receivers IMMEDIATELY for instant display (no setTimeout delay)
  if (rt.receivers.length > 0) {
    var r = rt.receivers.slice(0);
    for (var i = 0; i < r.length; i++) {
      if (r[i].receiveReport !== undefined) r[i].receiveReport(report);
    }
  }
  // NOTE: Removed scheduleAccountDataFetch() - reports provide instant updates
  // AccountData is fetched on initial connection and for final states (filled/cancelled) in orderStore
};
